import { AxiosResponse } from 'axios';
import sectionKeys from 'helpers/sectionKeys';
import { unschema } from '../../../schema';
import * as ActionTypes from './actionTypes';

interface APIError {
  message?: string
  code?: string
}

//
// ─── FETCH IDENTITY STATUS ───────────────────────────────────────────────────
//

export type IdentityStatusAccount = {
  CreatedDate: string,
  ExternalId: string,
  FormType: string,
  FormVersion: string,
  OrgName: string,
  PocEmail: string,
  PocName: string,
  Status: string,
};

export type FetchIdentityStatusData = {
  accounts: IdentityStatusAccount[];
};

export interface FetchIdentityStatusResponse extends AxiosResponse {
  data: FetchIdentityStatusData;
}

export interface FetchIdentityStatusFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchIdentityStatusRequestAction {
  type: typeof ActionTypes.FETCH_IDENTITY_STATUS_REQUEST
}

export const fetchIdentityStatusRequest = (): FetchIdentityStatusRequestAction => ({
  type: ActionTypes.FETCH_IDENTITY_STATUS_REQUEST,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchIdentityStatusSuccessAction {
  type: typeof ActionTypes.FETCH_IDENTITY_STATUS_SUCCESS;
  payload: FetchIdentityStatusData;
}

export const fetchIdentityStatusSuccess = (response: FetchIdentityStatusResponse): FetchIdentityStatusSuccessAction => ({
  type: ActionTypes.FETCH_IDENTITY_STATUS_SUCCESS,
  payload: response.data,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchIdentityStatusFailureAction {
  type: typeof ActionTypes.FETCH_IDENTITY_STATUS_FAILURE
  payload: {
    error: object
  }
}

export const fetchIdentityStatusFailure = (error: FetchIdentityStatusFailureResponse): FetchIdentityStatusFailureAction => ({
  type: ActionTypes.FETCH_IDENTITY_STATUS_FAILURE,
  payload: {
    error,
  },
});

//
// ─── FETCH SET ACCOUNT ───────────────────────────────────────────────────────
//

export type FetchSetAccountSuccessResponse = AxiosResponse;

export interface FetchSetAccountFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchSetAccountRequestAction {
  type: typeof ActionTypes.FETCH_SET_ACCOUNT_REQUEST;
  payload: { requestId: string, status: string };
}

export const fetchSetAccountRequest = (requestId: string, status: string): FetchSetAccountRequestAction => ({
  type: ActionTypes.FETCH_SET_ACCOUNT_REQUEST,
  payload: { requestId, status },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchSetAccountSuccessAction {
  type: typeof ActionTypes.FETCH_SET_ACCOUNT_SUCCESS;
  status: string;
}

export const fetchSetAccountSuccess = (_response: FetchSetAccountSuccessResponse, status: string): FetchSetAccountSuccessAction => ({
  type: ActionTypes.FETCH_SET_ACCOUNT_SUCCESS,
  status,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchSetAccountFailureAction {
  type: typeof ActionTypes.FETCH_SET_ACCOUNT_FAILURE;
  payload: { error: object };
}

export const fetchSetAccountFailure = (error: FetchSetAccountFailureResponse): FetchSetAccountFailureAction => ({
  type: ActionTypes.FETCH_SET_ACCOUNT_FAILURE,
  payload: { error },
});

//
// ─── FETCH STATUS ───────────────────────────────────────────────────────────────
//

export interface StatusResponse extends AxiosResponse {
  data: {
    Status: string
    Hash: string
    LastLogin: string
    FormType: string
    FormVersion: string
    PDFExpiration: string
    LastSubmissionDate: object
  }
}

export interface StatusFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchStatusRequestAction {
  type: typeof ActionTypes.FETCH_STATUS_REQUEST
}
export const fetchStatusRequest = (): FetchStatusRequestAction => ({
  type: ActionTypes.FETCH_STATUS_REQUEST,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchStatusSuccessAction {
  type: typeof ActionTypes.FETCH_STATUS_SUCCESS
  payload: {
    lastLogin: string
    formType: string
    formVersion: string
    hash: string
    status: string
    pdfExpiration: string
    lastSubmissionDate: object
  }
}
export const fetchStatusSuccess = (response: StatusResponse): FetchStatusSuccessAction => ({
  type: ActionTypes.FETCH_STATUS_SUCCESS,
  payload: {
    lastLogin: response.data.LastLogin,
    formType: response.data.FormType,
    formVersion: response.data.FormVersion,
    hash: response.data.Hash,
    status: response.data.Status,
    pdfExpiration: response.data.PDFExpiration,
    lastSubmissionDate: response.data.LastSubmissionDate,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchStatusFailureAction {
  type: typeof ActionTypes.FETCH_STATUS_FAILURE
  payload: {
    error: object
  }
}
export const fetchStatusFailure = (error: StatusFailureResponse): FetchStatusFailureAction => ({
  type: ActionTypes.FETCH_STATUS_FAILURE,
  payload: {
    error,
  },
});

//
// ─── FETCH ENVIRONMENT ───────────────────────────────────────────────────────────────
//

export interface EnvironmentResponse extends AxiosResponse {
  data: {
    LoginRedirect: string,
    APIBaseURL: string,
    APIRedirect: string,
  }
}

export interface EnvironmentFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchEnvironmentRequestAction {
  type: typeof ActionTypes.FETCH_ENVIRONMENT_REQUEST
}
export const fetchEnvironmentRequest = (): FetchEnvironmentRequestAction => ({
  type: ActionTypes.FETCH_ENVIRONMENT_REQUEST,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchEnvironmentSuccessAction {
  type: typeof ActionTypes.FETCH_ENVIRONMENT_SUCCESS
  payload: {
    loginRedirect: string,
    apiBaseUrl: string,
    apiRedirect: string,
  }
}
export const fetchEnvironmentSuccess = (response: EnvironmentResponse): FetchEnvironmentSuccessAction => ({
  type: ActionTypes.FETCH_ENVIRONMENT_SUCCESS,
  payload: {
    // TODO: remove this environment call once all the variables are available in all the Front end environments.
    loginRedirect: response.data.LoginRedirect, // Not available in the INT environment FE possibly others as of October
    apiBaseUrl: process.env.API_BASE_URL || '',
    apiRedirect: response.data.APIRedirect, // This is completely Unused and references the FrontEnd Server name
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchEnvironmentFailureAction {
  type: typeof ActionTypes.FETCH_ENVIRONMENT_FAILURE
  payload: {
    error: object
  }
}
export const fetchEnvironmentFailure = (error: EnvironmentFailureResponse): FetchEnvironmentFailureAction => ({
  type: ActionTypes.FETCH_ENVIRONMENT_FAILURE,
  payload: {
    error,
  },
});

//
// ─── FETCH FORM ─────────────────────────────────────────────────────────────────
//

interface FormResponseBaseData {
  Metadata: {
    form_type: string // eslint-disable-line camelcase
    form_version: string // eslint-disable-line camelcase
    type: string
  }
}

/**
 * This identifies a section associated to the FormResponse.
 */
export interface FormResponseSubsectionData {
  type: string
  props: object
}

interface FormResponseSection {
  [key: string]: {
    [key: string]: FormResponseSubsectionData
  }
}

type FormResponseData = FormResponseBaseData & FormResponseSection
export interface FormResponse extends AxiosResponse {
  data: FormResponseData
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchFormRequestAction {
  type: typeof ActionTypes.FETCH_FORM_REQUEST
}
export const fetchFormRequest = (): FetchFormRequestAction => ({
  type: ActionTypes.FETCH_FORM_REQUEST,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface NormalizedFormData {
  [key: string]: {
    data: object
    subsectionName: string
    subsectionKey: string
    sectionName: string
  }
}

export interface FetchFormSuccessAction {
  type: typeof ActionTypes.FETCH_FORM_SUCCESS
  payload: {
    formType: string
    formVersion: string
    data: FormResponse['data']
    normalized: NormalizedFormData
  }
}
export const fetchFormSuccess = (response: FormResponse): FetchFormSuccessAction => {
  const normalized: NormalizedFormData = {};

  // This is a rework of the old updateSectionDataLegacy. We perform the
  // normalization much earlier (in this action) and dispatch this single
  // action. From there, reducers listen to it and update the state accordingly.
  // This minimizes wasted render resources, as the state is only updated once
  // (regardless of domain) versus a bunch. While this can be cleaned up and
  // optimized further, it should help significantly.

  Object.keys(response.data).forEach((sectionName) => {
    const section = response.data[sectionName];

    if (sectionName === 'Metadata') { return; }

    Object.keys(section).forEach((subsectionName) => {
      let subsectionKey = sectionKeys[`${sectionName}.${subsectionName}` as keyof typeof sectionKeys];
      const sectionData = unschema(section[subsectionName], response.data?.Metadata);

      if (subsectionKey === undefined) { subsectionKey = `${sectionName}_${subsectionName}`.toLocaleUpperCase(); }

      normalized[subsectionKey] = {
        data: sectionData,
        sectionName,
        subsectionName,
        subsectionKey,
      };
    });
  });

  return {
    type: ActionTypes.FETCH_FORM_SUCCESS,
    payload: {
      formType: response.data.Metadata.form_type,
      formVersion: response.data.Metadata.form_version,
      data: response.data,
      normalized,
    },
  };
};

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchFormFailureAction {
  type: typeof ActionTypes.FETCH_FORM_FAILURE
  payload: {
    error: object
  }
}
export const fetchFormFailure = (response: any): FetchFormFailureAction => ({
  type: ActionTypes.FETCH_FORM_FAILURE,
  payload: {
    error: response,
  },
});

//
// ─── FETCH API Version ───────────────────────────────────────────────────────────────
//

export interface ApiVersionResponse extends AxiosResponse {
  data: {
    apiVersionText: string,
  }
}

export interface ApiVersionFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchApiVersionRequestAction {
  type: typeof ActionTypes.FETCH_API_VERSION_REQUEST
}
export const fetchApiVersionRequest = (): FetchApiVersionRequestAction => ({
  type: ActionTypes.FETCH_API_VERSION_REQUEST,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchApiVersionSuccessAction {
  type: typeof ActionTypes.FETCH_API_VERSION_SUCCESS
  payload: {
    apiVersionText: string,
  }
}
export const fetchApiVersionSuccess = (response: ApiVersionResponse): FetchApiVersionSuccessAction => ({
  type: ActionTypes.FETCH_API_VERSION_SUCCESS,
  payload: {
    apiVersionText: response.data.apiVersionText,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchApiVersionFailureAction {
  type: typeof ActionTypes.FETCH_API_VERSION_FAILURE
  payload: {
    error: object
  }
}
export const fetchApiVersionFailure = (error: ApiVersionFailureResponse): FetchApiVersionFailureAction => ({
  type: ActionTypes.FETCH_API_VERSION_FAILURE,
  payload: {
    error,
  },
});

//
// ─── FETCH SERVER TIME ──────────────────────────────────────────────────────────────────────
//

export interface ServerTimeResponse extends AxiosResponse {
  data: {
    serverTime: object,
  }
}

export interface ServerTimeFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchServerTimeRequestAction {
  type: typeof ActionTypes.FETCH_API_VERSION_REQUEST
}
export const fetchServerTimeRequest = (): FetchServerTimeRequestAction => ({
  type: ActionTypes.FETCH_API_VERSION_REQUEST,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchServerTimeSuccessAction {
  type: typeof ActionTypes.FETCH_SERVER_TIME_SUCCESS
  payload: {
    serverTime: object,
  }
}
export const fetchServerTimeSuccess = (response: ServerTimeResponse): FetchServerTimeSuccessAction => ({
  type: ActionTypes.FETCH_SERVER_TIME_SUCCESS,
  payload: {
    serverTime: response.data.serverTime,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchServerTimeFailureAction {
  type: typeof ActionTypes.FETCH_SERVER_TIME_FAILURE
  payload: {
    error: object
  }
}
export const fetchServerTimeFailure = (error: ServerTimeFailureResponse): FetchServerTimeFailureAction => ({
  type: ActionTypes.FETCH_SERVER_TIME_FAILURE,
  payload: {
    error,
  },
});

//
// ─── FETCH Institution Names ───────────────────────────────────────────────────────────────
//

export interface InstitutionNamesResponse extends AxiosResponse {
  data: {
    institutionNames: string[],
  }
}

export interface InstitutionNamesFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionNamesRequestAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_NAMES_REQUEST,
  payload: {
    searchtext: string,
  }
}
export const fetchInstitutionNamesRequest = (findText: string): FetchInstitutionNamesRequestAction => ({
  type: ActionTypes.FETCH_INSTITUTION_NAMES_REQUEST,
  payload: {
    searchtext: findText,
  }
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionNamesSuccessAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_NAMES_SUCCESS
  payload: {
    institutionNames: string[],
  }
}
export const fetchInstitutionNamesSuccess = (response: InstitutionNamesResponse): FetchInstitutionNamesSuccessAction => ({
  type: ActionTypes.FETCH_INSTITUTION_NAMES_SUCCESS,
  payload: {
    institutionNames: response.data.institutionNames,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionNamesFailureAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_NAMES_FAILURE
  payload: {
    error: object
  }
}
export const fetchInstitutionNamesFailure = (error: InstitutionNamesFailureResponse): FetchInstitutionNamesFailureAction => ({
  type: ActionTypes.FETCH_INSTITUTION_NAMES_FAILURE,
  payload: {
    error,
  },
});

//
// ─── FETCH Institution Addresses ───────────────────────────────────────────────────────────────
//

export interface InstitutionAddressesResponse extends AxiosResponse {
  data: {
    institutions: object,
  }
}

export interface InstitutionAddressesFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionAddressesRequestAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_ADDRESSES_REQUEST,
  payload: {
    searchtext: string,
  }
}
export const fetchInstitutionAddressesRequest = (findText: string): FetchInstitutionAddressesRequestAction => ({
  type: ActionTypes.FETCH_INSTITUTION_ADDRESSES_REQUEST,
  payload: {
    searchtext: findText,
  }
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionAddressesSuccessAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_ADDRESSES_SUCCESS
  payload: {
    institutions: object,
  }
}
export const fetchInstitutionAddressesSuccess = (response: InstitutionAddressesResponse): FetchInstitutionAddressesSuccessAction => ({
  type: ActionTypes.FETCH_INSTITUTION_ADDRESSES_SUCCESS,
  payload: {
    institutions: response.data.institutions,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionAddressesFailureAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_ADDRESSES_FAILURE
  payload: {
    error: object
  }
}
export const fetchInstitutionAddressesFailure = (error: InstitutionAddressesFailureResponse): FetchInstitutionAddressesFailureAction => ({
  type: ActionTypes.FETCH_INSTITUTION_ADDRESSES_FAILURE,
  payload: {
    error,
  },
});

//
// ─── FETCH Institution Search ───────────────────────────────────────────────────────────────
//

export interface InstitutionSearchResponse extends AxiosResponse {
  data: {
    institutions: object,
  }
}

export interface InstitutionSearchFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionSearchRequestAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_SEARCH_REQUEST
  payload: {
    type: string,
    props: {
      searchText: string,
    }
  }
}
export const fetchInstitutionSearchRequest = (findText: string): FetchInstitutionSearchRequestAction => ({
  type: ActionTypes.FETCH_INSTITUTION_SEARCH_REQUEST,
  payload: {
    type: 'educationLocation',
    props: {
      searchText: findText
    }
  }
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionSearchSuccessAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_SEARCH_SUCCESS
  payload: {
    institutions: object,
  }
}
export const fetchInstitutionSearchSuccess = (response: InstitutionSearchResponse): FetchInstitutionSearchSuccessAction => ({
  type: ActionTypes.FETCH_INSTITUTION_SEARCH_SUCCESS,
  payload: {
    institutions: response.data.institutions,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchInstitutionSearchFailureAction {
  type: typeof ActionTypes.FETCH_INSTITUTION_SEARCH_FAILURE
  payload: {
    error: object
  }
}
export const fetchInstitutionSearchFailure = (error: InstitutionSearchFailureResponse): FetchInstitutionSearchFailureAction => ({
  type: ActionTypes.FETCH_INSTITUTION_SEARCH_FAILURE,
  payload: {
    error,
  },
});

//
// ─── FETCH Announcements ───────────────────────────────────────────────────────────────
//

export type Announcement = {
  title: string,
  content: string,
  priority: number
}

export type Announcements = {
  announcements: Announcement[]
}

export interface AnnouncementsResponse extends AxiosResponse {
  data: Announcements;
}

export interface AnnouncementsFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchAnnouncementsRequestAction {
  type: typeof ActionTypes.FETCH_ANNOUNCEMENTS_REQUEST
}
export const fetchAnnouncementsRequest = (): FetchAnnouncementsRequestAction => ({
  type: ActionTypes.FETCH_ANNOUNCEMENTS_REQUEST
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchAnnouncementsSuccessAction {
  type: typeof ActionTypes.FETCH_ANNOUNCEMENTS_SUCCESS
  payload: Announcements;
}
export const fetchAnnouncementsSuccess = (response: AnnouncementsResponse): FetchAnnouncementsSuccessAction => ({
  type: ActionTypes.FETCH_ANNOUNCEMENTS_SUCCESS,
  payload: {
    announcements: response.data.announcements,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchAnnouncementsFailureAction {
  type: typeof ActionTypes.FETCH_ANNOUNCEMENTS_FAILURE
  payload: {
    error: object
  }
}
export const fetchAnnouncementsFailure = (error: AnnouncementsFailureResponse): FetchAnnouncementsFailureAction => ({
  type: ActionTypes.FETCH_ANNOUNCEMENTS_FAILURE,
  payload: {
    error,
  },
});

//
// ─── CHECK STATUS ───────────────────────────────────────────────────
//

export type FetchCheckStatusResponseData = {
  verifyPassword: boolean,
};

export interface FetchCheckStatusResponse extends AxiosResponse {
  data: FetchCheckStatusResponseData;
}

export interface FetchCheckStatusFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchCheckStatusRequestAction {
  type: typeof ActionTypes.FETCH_CHECK_STATUS_REQUEST;
}

export const fetchCheckStatusRequest = (): FetchCheckStatusRequestAction => ({
  type: ActionTypes.FETCH_CHECK_STATUS_REQUEST
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchCheckStatusSuccessAction {
  type: typeof ActionTypes.FETCH_CHECK_STATUS_SUCCESS;
  payload: FetchCheckStatusResponseData;
}

export const fetchCheckStatusSuccess = (response: FetchCheckStatusResponse): FetchCheckStatusSuccessAction => ({
  type: ActionTypes.FETCH_CHECK_STATUS_SUCCESS,
  payload: response.data,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface FetchCheckStatusFailureAction {
  type: typeof ActionTypes.FETCH_CHECK_STATUS_FAILURE
  payload: {
    error: object
  }
}

export const fetchCheckStatusFailure = (error: FetchCheckStatusFailureResponse): FetchCheckStatusFailureAction => ({
  type: ActionTypes.FETCH_CHECK_STATUS_FAILURE,
  payload: {
    error,
  },
});

//
// ─── SUBMIT IDENTITY VERIFICATION ───────────────────────────────────────────────────
//
export type IdentityVerificationRequestData = {
  SSN: string,
  DOB: string,
};

export type SubmitIdentityVerificationResponseData = {
  success: boolean,
};

export interface SubmitIdentityVerificationResponse extends AxiosResponse {
  data: SubmitIdentityVerificationResponseData;
}

export interface SubmitIdentityVerificationFailureResponse extends AxiosResponse {
  data: {
    errors: APIError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface SubmitIdentityVerificationRequestAction {
  type: typeof ActionTypes.SUBMIT_IDENTITY_VERIFICATION_REQUEST
  payload: IdentityVerificationRequestData
}

export const submitIdentityVerificationRequest = (requestData: IdentityVerificationRequestData): SubmitIdentityVerificationRequestAction => ({
  type: ActionTypes.SUBMIT_IDENTITY_VERIFICATION_REQUEST,
  payload: requestData
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface SubmitIdentityVerificationSuccessAction {
  type: typeof ActionTypes.SUBMIT_IDENTITY_VERIFICATION_SUCCESS;
  payload: SubmitIdentityVerificationResponseData;
}

export const submitIdentityVerificationSuccess = (response: SubmitIdentityVerificationResponse): SubmitIdentityVerificationSuccessAction => ({
  type: ActionTypes.SUBMIT_IDENTITY_VERIFICATION_SUCCESS,
  payload: response.data,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface SubmitIdentityVerificationFailureAction {
  type: typeof ActionTypes.SUBMIT_IDENTITY_VERIFICATION_FAILURE
  payload: {
    error: object
  }
}

export const submitIdentityVerificationFailure = (error: SubmitIdentityVerificationFailureResponse): SubmitIdentityVerificationFailureAction => ({
  type: ActionTypes.SUBMIT_IDENTITY_VERIFICATION_FAILURE,
  payload: {
    error,
  },
});

//
// ─── OTHER ──────────────────────────────────────────────────────────────────────
//

export type APIActions =
  | FetchCheckStatusFailureAction
  | FetchCheckStatusRequestAction
  | FetchCheckStatusSuccessAction
  | FetchIdentityStatusFailureAction
  | FetchIdentityStatusRequestAction
  | FetchIdentityStatusSuccessAction
  | FetchSetAccountFailureAction
  | FetchSetAccountRequestAction
  | FetchSetAccountSuccessAction
  | FetchStatusFailureAction
  | FetchStatusRequestAction
  | FetchStatusSuccessAction
  | FetchFormRequestAction
  | FetchFormSuccessAction
  | FetchFormFailureAction
  | FetchEnvironmentRequestAction
  | FetchEnvironmentSuccessAction
  | FetchEnvironmentFailureAction
  | FetchApiVersionRequestAction
  | FetchApiVersionSuccessAction
  | FetchApiVersionFailureAction
  | FetchServerTimeRequestAction
  | FetchServerTimeSuccessAction
  | FetchServerTimeFailureAction
  | FetchInstitutionAddressesRequestAction
  | FetchInstitutionAddressesSuccessAction
  | FetchInstitutionAddressesFailureAction
  | FetchInstitutionNamesRequestAction
  | FetchInstitutionNamesSuccessAction
  | FetchInstitutionNamesFailureAction
  | FetchInstitutionSearchRequestAction
  | FetchInstitutionSearchSuccessAction
  | FetchInstitutionSearchFailureAction
  | FetchAnnouncementsRequestAction
  | FetchAnnouncementsSuccessAction
  | FetchAnnouncementsFailureAction
  | SubmitIdentityVerificationFailureAction
  | SubmitIdentityVerificationRequestAction
  | SubmitIdentityVerificationSuccessAction;
